import { useLayoutStore } from '@src/stores-v2/StoreContext';
import { useCallback, useEffect, useRef } from 'react';

import { getDocumentHeight } from '../utility/general';

export function useTimelineLayoutResize() {
  const layoutStore = useLayoutStore();
  const { timelineHeight, isDragging } = layoutStore;

  const initialMouseY = useRef<number | null>(null);
  const initialHeight = useRef<number | null>(null);
  const latestClientY = useRef<number | null>(null);
  const rafId = useRef<number | null>(null);

  const applyMove = useCallback(
    (clientY: number) => {
      if (initialMouseY.current === null || initialHeight.current === null) {
        return;
      }
      const documentHeight = getDocumentHeight();

      const currentHeight = initialHeight.current * 0.01 * documentHeight;
      const deltaY = initialMouseY.current - clientY;

      const updatedHeight = currentHeight + deltaY;
      const updatedHeightPercent = (updatedHeight / documentHeight) * 100;

      const newHeightPercentage = Math.min(
        Math.max(updatedHeightPercent, 10),
        100,
      );

      layoutStore.setTimelineHeight(newHeightPercentage);
    },
    [layoutStore],
  );

  // Batch moves to one update per animation frame so the drag tracks the
  // pointer smoothly without flooding the renderer.
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      latestClientY.current = e.clientY;
      if (rafId.current === null) {
        rafId.current = requestAnimationFrame(() => {
          rafId.current = null;
          if (latestClientY.current !== null) {
            applyMove(latestClientY.current);
          }
        });
      }
    },
    [applyMove],
  );

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      initialMouseY.current = e.clientY;
      initialHeight.current = parseFloat(timelineHeight);
      // Toggle the shared layout drag flag so the video player gets
      // pointer-events:none during the drag — otherwise the Creatomate iframe
      // swallows the mouseup when the cursor passes over it and the bar sticks.
      layoutStore.setIsDragging(true);
    },
    [timelineHeight, layoutStore],
  );

  const handleMouseUp = useCallback(() => {
    // Flush the final pointer position before clearing drag state, otherwise
    // the last frame's movement is lost and the bar snaps to a stale spot.
    if (rafId.current !== null) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
    if (latestClientY.current !== null) {
      applyMove(latestClientY.current);
    }
    initialMouseY.current = null;
    initialHeight.current = null;
    latestClientY.current = null;
    layoutStore.setIsDragging(false);
  }, [applyMove, layoutStore]);

  // Cancel any pending frame if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  return {
    isDragging,
    handleMouseMove,
    handleMouseDown,
    handleMouseUp,
  };
}
